//
// Copyright 2024 DXOS.org
//

import * as Context from 'effect/Context';

import { invariant } from '@dxos/invariant';
import { log } from '@dxos/log';
import { subscribeStream } from '@dxos/protocols';
import { toPublicKey } from '@dxos/protocols/buf';
import { DeviceKind } from '@dxos/protocols/buf/dxos/client/services_pb';
import { type DevicesService, type IdentityService } from '@dxos/protocols/rpc';

export const setIdentityTags = ({
  identityService,
  devicesService,
  runtime = Context.empty(),
  setTag,
}: {
  identityService: IdentityService.Client;
  devicesService: DevicesService.Client;
  runtime?: Context.Context<never>;
  setTag: (k: string, v: string) => void;
}) => {
  subscribeStream(runtime, identityService['IdentityService.queryIdentity'](undefined), {
    onData: (idqr) => {
      if (!idqr?.identity?.identityKey) {
        log('empty response from identity service', { idqr });
        return;
      }

      setTag('identityKey', toPublicKey(idqr.identity.identityKey)?.truncate() ?? '');
    },
  });

  subscribeStream(runtime, devicesService['DevicesService.queryDevices'](undefined), {
    onData: (dqr) => {
      if (!dqr || !dqr.devices || dqr.devices.length === 0) {
        log('empty response from device service', { device: dqr });
        return;
      }
      invariant(dqr, 'empty response from device service');

      const thisDevice = dqr.devices.find((device) => device.kind === DeviceKind.CURRENT);
      if (!thisDevice) {
        log('no current device', { device: dqr });
        return;
      }
      setTag('deviceKey', toPublicKey(thisDevice.deviceKey)?.truncate() ?? '');
    },
  });
};
